import React, { useMemo, useState } from "react"
import _ from "lodash"
import {
  Box, Flex, Input, Text,
} from "@chakra-ui/react"
import { Combobox } from "components/Combobox"
import Icon from "components/elements/Icon"
import {
  Form, FormQuestion, SegmentRuleField, SegmentRuleOperator,
} from "sharedTypes"
import { stripHtmlTags } from "utilities/strings"
import { buildFormQuestionConfig, isFilterableQuestionType } from "./config"
import { FilterFieldConfig, FilterOptionsContext } from "./types"
import MultiValuePicker from "./MultiValuePicker"
import NumberPicker from "./NumberPicker"
import DatePicker from "./DatePicker"

type FieldItem = { kind: "field"; config: FilterFieldConfig; disabled: boolean }
type QuestionItem = { kind: "question"; question: FormQuestion }

// Four-step picker state machine:
//   "field"            → step 1: searchable list of filter fields
//   "question"         → step 2a: searchable list of form questions (Form Answers sentinel)
//   "formSubmission"   → step 2b: form multi-picker (Form Submission sentinel)
//   "submissionStatus" → step 3b: Submitted / Not Submitted single-select
//   "value"            → step 3a/final: value editor for the resolved FilterFieldConfig
//
// `prevStep` on "value" tells the back-button where to return.
type Step =
  | { kind: "field" }
  | { kind: "question" }
  | { kind: "formSubmission" }
  | { kind: "submissionStatus"; formIds: string[] }
  | { kind: "value"; config: FilterFieldConfig; prevStep: "field" | "question" }

type Props = {
  fieldConfigs: FilterFieldConfig[]
  context: FilterOptionsContext
  disabledFields: Set<string>
  onAdd: (field: string, values: string[], operator?: SegmentRuleOperator) => void
  // Loads the next page of form questions when the sub-picker nears its
  // bottom — mirrors the other useEventQuestions callers.
  onQuestionsScrollEnd?: () => void
  children: React.ReactElement
}

// Shared back-button + centered-title header used for steps 2 and 3.
const StepHeader = ({
  title, onBack,
}: { title: string; onBack: () => void }) => (
  <Flex align="center" w="full">
    <Flex
      as="button"
      align="center"
      justify="center"
      w={6}
      color="blue.500"
      onClick={onBack}
      aria-label="Back"
    >
      <Icon icon="prev-arrow" />
    </Flex>
    <Box flex={1} minW={0} textAlign="center" fontSize="md">
      <Text noOfLines={1} title={title}>{title}</Text>
    </Box>
    <Box w={6} />
  </Flex>
)

// Matches ColumnVisibilityMenu's row style: gray.600 leading icon, plain text.
const FieldRow = ({ config }: { config: FilterFieldConfig }) => (
  <Flex align="center" w="full">
    <Box mr={3} color="gray.600" display="inline-flex">{config.icon}</Box>
    <Text flex={1} minW={0} noOfLines={1} title={config.label}>{config.label}</Text>
  </Flex>
)

const getFieldItemKey = (item: FieldItem): string => `field-${item.config.field}`
const getFieldItemLabel = (item: FieldItem): string => item.config.label

const getQuestionItemKey = (item: QuestionItem): string => `question-${item.question.id}`
const getQuestionItemLabel = (item: QuestionItem): string => stripHtmlTags(item.question.title)

const AddFilterPopover = ({
  fieldConfigs, context, disabledFields, onAdd, onQuestionsScrollEnd, children,
}: Props) => {
  const [step, setStep] = useState<Step>({ kind: "field" })
  const [textDraft, setTextDraft] = useState("")

  const handleQuestionsScroll = useMemo(
    () => _.debounce((e: Event) => {
      const target = e.target as HTMLElement
      if (target.scrollHeight - target.scrollTop - target.clientHeight < 10) {
        onQuestionsScrollEnd?.()
      }
    }, 100),
    [onQuestionsScrollEnd],
  )

  // Step 1: field items. Empty in other steps so Combobox clears its search
  // input on transition and arrow-key nav only covers visible rows.
  const fieldItems: FieldItem[] = useMemo(() => {
    if (step.kind !== "field") return []

    return fieldConfigs
      .filter((config) => !config.hiddenFromPicker)
      .map((config) => ({
        kind: "field",
        config,
        disabled: disabledFields.has(config.field),
      }))
  }, [step.kind, fieldConfigs, disabledFields])

  // Step 2: filterable questions from context.
  const questionItems: QuestionItem[] = useMemo(() => {
    if (step.kind !== "question") return []
    const questions = context.formQuestions ?? []

    return questions
      .filter((q) => isFilterableQuestionType(q.type))
      .map((q) => ({ kind: "question" as const, question: q }))
  }, [step.kind, context.formQuestions])

  const handleFieldChange = (item: FieldItem | null): boolean | void => {
    if (!item || item.disabled) return false

    if (item.config.isFormQuestionCategory) {
      setStep({ kind: "question" })
    } else if (item.config.isFormSubmissionCategory) {
      setStep({ kind: "formSubmission" })
    } else {
      setStep({ kind: "value", config: item.config, prevStep: "field" })
    }

    return false
  }

  const handleQuestionChange = (item: QuestionItem | null): boolean | void => {
    if (!item) return false
    const config = buildFormQuestionConfig(item.question)
    setStep({ kind: "value", config, prevStep: "question" })

    return false
  }

  // Reset to step 1 when popover opens.
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setStep({ kind: "field" })
      setTextDraft("")
    }
  }

  const goBack = () => {
    if (step.kind === "question" || step.kind === "formSubmission") {
      setStep({ kind: "field" })
    } else if (step.kind === "submissionStatus") {
      setStep({ kind: "formSubmission" })
    } else if (step.kind === "value") {
      setStep(step.prevStep === "question" ? { kind: "question" } : { kind: "field" })
    }
  }

  // Commits a value array and resets to step 1.
  const applyAndClose = (values: string[]) => {
    if (step.kind !== "value") return
    if (values.length === 0) {
      goBack()

      return
    }
    onAdd(step.config.field, values)
    setStep({ kind: "field" })
  }

  // Text fields (contains / does not contain): commit from inline input.
  const applyText = () => {
    if (step.kind !== "value") return
    const trimmed = textDraft.trim()
    if (trimmed) onAdd(step.config.field, [trimmed])
    setTextDraft("")
    setStep({ kind: "field" })
  }

  const inStep1 = step.kind === "field"
  const inStep2Question = step.kind === "question"
  const inStep2Form = step.kind === "formSubmission"
  const inStep3Status = step.kind === "submissionStatus"
  const inStepValue = step.kind === "value"
  const valueConfig = inStepValue ? step.config : null
  const formStatusIds = step.kind === "submissionStatus" ? step.formIds : []

  const formOptions = useMemo(
    () => (context.forms ?? []).map((f: Form) => ({ label: f.name, value: f.id.toString() })),
    [context.forms],
  )

  // arrowBg matches the popover arrow to the content background:
  // all steps except step 1 show a header (gray.50).
  const arrowBg = inStep1 ? "white" : "gray.50"

  return (
    <Combobox.Root<FieldItem>
      items={fieldItems}
      getItemKey={getFieldItemKey}
      getItemLabel={getFieldItemLabel}
      onChange={handleFieldChange}
      onOpenChange={handleOpenChange}
      // Step 3 uses Combobox.Action rows — without this opt-out the "no items
      // but actions exist" branch would pre-highlight the first row on mount.
      autoHighlightFirstAction={false}
    >
      <Combobox.Trigger>{children}</Combobox.Trigger>
      <Combobox.Content w="280px" arrowBg={arrowBg} growToContent>

        {/* ── Step 1: field list ───────────────────────────────────────── */}
        {inStep1 && (
          <>
            <Combobox.Input placeholder="Search filter" py={2} />
            <Combobox.List<FieldItem> maxH="60vh">
              {(item) => (
                <Combobox.Item
                  item={item}
                  hideCheckmark
                  disabled={item.disabled}
                  data-testid={`add-filter-field-${item.config.field}`}
                >
                  <FieldRow config={item.config} />
                </Combobox.Item>
              )}
            </Combobox.List>
            <Combobox.Empty>
              {(query) => (
                <Text textAlign="center" color="gray.500" py={4} fontSize="md">
                  {query ? `No matches for "${query}"` : "No options available"}
                </Text>
              )}
            </Combobox.Empty>
          </>
        )}

        {/* ── Step 2a: question sub-picker (Form Answers) ─────────────── */}
        {inStep2Question && (
          <>
            <Combobox.Header>
              <StepHeader title="Form Answers" onBack={goBack} />
            </Combobox.Header>
            {/* Nested Combobox.Root gives the question list its own search
                input state, independent of the outer step-1 root. The outer
                Combobox.Root's `items` is [] here, so its keyboard nav only
                covers the inner Combobox.Action rows. */}
            <Combobox.Root<QuestionItem>
              items={questionItems}
              getItemKey={getQuestionItemKey}
              getItemLabel={getQuestionItemLabel}
              onChange={handleQuestionChange}
              autoHighlightFirstAction={false}
            >
              <Combobox.Input placeholder="Search questions" py={2} autoFocus />
              <Combobox.List<QuestionItem> maxH="60vh" onScroll={handleQuestionsScroll}>
                {(item) => {
                  const title = stripHtmlTags(item.question.title)

                  return (
                    <Combobox.Item item={item} hideCheckmark>
                      <Flex align="center" w="full">
                        <Box mr={3} color="gray.600" display="inline-flex">
                          <Icon icon="form" />
                        </Box>
                        <Text flex={1} minW={0} noOfLines={1} title={title}>{title}</Text>
                      </Flex>
                    </Combobox.Item>
                  )
                }}
              </Combobox.List>
              <Combobox.Empty>
                {(query) => (
                  <Text textAlign="center" color="gray.500" py={4} fontSize="md">
                    {query ? `No matches for "${query}"` : "No questions available"}
                  </Text>
                )}
              </Combobox.Empty>
            </Combobox.Root>
          </>
        )}

        {/* ── Step 2b: form multi-picker (Form Submission sentinel) ───── */}
        {inStep2Form && (
          <>
            <Combobox.Header>
              <StepHeader title="Form Submission" onBack={goBack} />
            </Combobox.Header>
            <MultiValuePicker
              label="forms"
              options={formOptions}
              keepOpenOnCommit
              onCommit={(formIds) => {
                if (formIds.length === 0) {
                  goBack()
                } else {
                  setStep({ kind: "submissionStatus", formIds })
                }
              }}
            />
          </>
        )}

        {/* ── Step 3b: Submitted / Not Submitted single-select ────────── */}
        {inStep3Status && (
          <>
            <Combobox.Header>
              <StepHeader title="Form Submission" onBack={goBack} />
            </Combobox.Header>
            <Box py={1}>
              {([
                { label: "Submitted", field: SegmentRuleField.FormSubmitted },
                { label: "Not Submitted", field: SegmentRuleField.FormNotSubmitted },
              ] as const).map(({ label, field }) => (
                <Combobox.Action
                  key={field}
                  onSelect={() => {
                    onAdd(field, formStatusIds)
                    setStep({ kind: "field" })
                  }}
                  closeOnSelect
                  px={3}
                  py={2}
                >
                  <Text fontSize="md">{label}</Text>
                </Combobox.Action>
              ))}
            </Box>
          </>
        )}

        {/* ── Step 3a: value editor ────────────────────────────────────── */}
        {inStepValue && valueConfig && (
          <>
            <Combobox.Header>
              <StepHeader title={valueConfig.label} onBack={goBack} />
            </Combobox.Header>
            {valueConfig.type === "enum" && (
              <MultiValuePicker
                label={valueConfig.label}
                options={valueConfig.getOptions?.(context) ?? []}
                onCommit={applyAndClose}
                singleSelect={valueConfig.multiSelect === false}
              />
            )}
            {valueConfig.type === "number" && <NumberPicker onCommit={applyAndClose} />}
            {valueConfig.type === "date" && <DatePicker onCommit={applyAndClose} />}
            {valueConfig.type === "text" && (
              <Box px={3} py={2}>
                <Input
                  autoFocus
                  size="sm"
                  placeholder="Type to filter…"
                  value={textDraft}
                  onChange={(e) => setTextDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") applyText()
                  }}
                  onBlur={applyText}
                />
              </Box>
            )}
          </>
        )}

      </Combobox.Content>
    </Combobox.Root>
  )
}

export default AddFilterPopover
